import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";

import { US_DATE_FORMAT } from "./usDate";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);

type UsTimezone = {
  label: string;
  value: string;
  offset: string;
};

export type ParsedDateTime = string[];

export const DATETIME_FORMAT = "L, LT";
const TIMEZONE_OFFSET_REGEX = /([-+]\d{2}:\d{2})$/;
const PART_SEPARATOR = ", ";

export const DEFAULT_TIMEZONE = "America/New_York";

const US_TIMEZONES: UsTimezone[] = [
  {
    value: DEFAULT_TIMEZONE,
    label: "EST",
    offset: "-05:00",
  },
  {
    value: "America/Chicago",
    label: "CST",
    offset: "-06:00",
  },
  {
    value: "America/Phoenix",
    label: "MST",
    offset: "-07:00",
  },
  {
    value: "America/Los_Angeles",
    label: "PST",
    offset: "-08:00",
  },
];

export const getTimezoneByOffset = (offset: string): string => {
  const timeZone = US_TIMEZONES.find((option) => option.offset === offset)?.value;

  if (!timeZone) {
    throw new Error(`Offset ${offset} is not supported`);
  }

  return timeZone;
};

export const getOffset = (isoDateTime: string): string | null => {
  const matches = isoDateTime.match(TIMEZONE_OFFSET_REGEX);

  if (!matches) {
    return null;
  }

  const [ , offset ] = matches;

  return offset;
};

export const parseDateTime = (
  isoDateTime?: string | null,
): ParsedDateTime | "" | null | undefined => {
  if (!isoDateTime) {
    const emptyValue = isoDateTime as "" | null | undefined;

    return emptyValue;
  }

  const offset = getOffset(isoDateTime);

  if (!offset) {
    const value = dayjs(isoDateTime).format(DATETIME_FORMAT);
    const [ date, time ] = value.split(PART_SEPARATOR);

    return [ date, time ];
  }

  const timeZone = getTimezoneByOffset(offset);

  const format = `${US_DATE_FORMAT}, hh:mm a, z`;
  const realOffset = dayjs(isoDateTime).tz(timeZone).format("Z");
  const newIsoDateTime = isoDateTime.replace(offset, realOffset);

  const value = dayjs(newIsoDateTime).tz(timeZone).format(format);

  const [ date, time, zone ] = value.split(PART_SEPARATOR);
  const formattedValue = [ date, time, zone ].join(" ");

  return [ date, time, zone, formattedValue ];
};

export const getFormattedDateTimeWithTimezone = (isoDateTime?: string | null): string => {
  const parsedValues = parseDateTime(isoDateTime);

  if (!parsedValues) {
    return "";
  }

  const [ , , , formattedScheduledAt ] = parsedValues;

  const hasOffset = !!formattedScheduledAt;

  if (!hasOffset) {
    throw new Error(`Date time ${isoDateTime} has no timezone offset`);
  }

  return formattedScheduledAt;
};
